import { memo } from 'react'
import { 
  PhotoIcon, 
  CloudArrowUpIcon, 
  CheckBadgeIcon, 
  XMarkIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

/**
 * SightingImageGallery - Image gallery with moderation controls
 * 
 * @param {Object} sighting - Sighting data with images
 * @param {boolean} uploadingImages - Image upload in progress
 * @param {string} imageActionLoading - ID of image being moderated
 * @param {Function} onImageUpload - Callback for image upload
 * @param {Function} onApproveImage - Callback for approving image
 * @param {Function} onSetPrimaryImage - Callback for setting primary image
 * @param {Function} onDeleteImage - Callback for deleting image
 * 
 * @idempotent - Pure presentation component with React.memo
 * @atomic - Single responsibility: image gallery management (<250 lines)
 */
const SightingImageGallery = memo(function SightingImageGallery({
  sighting,
  uploadingImages,
  imageActionLoading,
  onImageUpload,
  onApproveImage,
  onSetPrimaryImage,
  onDeleteImage
}) {
  if (!sighting) return null

  const images = sighting.images || []
  const hasImages = images.length > 0

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Images ({images.length})
        </h3>
        
        <label className="cursor-pointer">
          <div className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors">
            <CloudArrowUpIcon className="h-5 w-5" />
            <span>{uploadingImages ? 'Uploading...' : 'Upload Images'}</span>
          </div>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={onImageUpload}
            className="hidden"
            disabled={uploadingImages}
          />
        </label>
      </div>

      {!hasImages ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">No images uploaded yet</p>
          <p className="text-sm text-gray-400 mt-1">Upload images to document this sighting</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image) => (
            <div 
              key={image.id} 
              className="relative group border-2 rounded-lg overflow-hidden hover:border-primary transition-colors"
            >
              <img
                src={image.imageUrl}
                alt={`Sighting ${image.imageType}`}
                className="w-full h-48 object-cover"
              />
              
              {/* Image badges */}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                {image.isPrimary && (
                  <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-medium">
                    <StarIconSolid className="h-3 w-3 mr-1" />
                    Primary
                  </span>
                )}
                
                {image.imageType === 'confirmation' && (
                  <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                    Confirmation
                  </span>
                )}
                
                {image.isApproved ? (
                  <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium">
                    <CheckBadgeIcon className="h-3 w-3 mr-1" />
                    Approved
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-medium">
                    Pending
                  </span>
                )}
              </div>

              {/* Image info */}
              {image.notes && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-3">
                  <p className="text-white text-sm line-clamp-2">{image.notes}</p>
                </div>
              )}

              {/* Action buttons - show on hover */}
              <div className="absolute inset-0 bg-black/50 can-hover:opacity-0 can-hover:group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!image.isApproved && (
                  <button
                    onClick={() => onApproveImage(image.id, false)}
                    disabled={imageActionLoading === image.id}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {imageActionLoading === image.id ? 'Processing...' : 'Approve'}
                  </button>
                )}
                
                {image.isApproved && (
                  <button
                    onClick={() => onApproveImage(image.id, true)}
                    disabled={imageActionLoading === image.id}
                    className="px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {imageActionLoading === image.id ? 'Processing...' : 'Unapprove'}
                  </button>
                )}
                
                {!image.isPrimary && (
                  <button
                    onClick={() => onSetPrimaryImage(image.id)}
                    disabled={imageActionLoading === image.id}
                    className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 text-sm font-medium"
                  >
                    <StarIcon className="h-4 w-4" />
                  </button>
                )}
                
                <button
                  onClick={() => onDeleteImage(image.id)}
                  disabled={imageActionLoading === image.id}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
              
              {/* Uploader info */}
              <div className="p-2 bg-gray-50 border-t">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>{image.uploaderName || 'Anonymous'}</span>
                  <span>{new Date(image.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

export default SightingImageGallery
